import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useEnterpriseEmulationState } from '@/hooks/useEnterpriseEmulation';
import { ToastAction } from '@/components/ui/toast';
import { isReconnectError } from './reconnect-error';

const PROVIDER_LABEL: Record<'gmail' | 'outlook', string> = {
  gmail: 'Gmail',
  outlook: 'Outlook',
};

/**
 * Returns `showIfReconnectError(err, providerType?)`: when `err` is a
 * token-can't-be-refreshed failure, shows an actionable "reconnect" toast and
 * returns true; otherwise returns false so the caller falls back to its own
 * generic error toast.
 *
 * Emulation-aware: under an ENTERPRISE emulation the acting admin can't fix the
 * member's OAuth, so we tell them who must reconnect and omit the action button
 * (uses useEnterpriseEmulationState, NOT the super-admin useEmulationGuard).
 */
export function useReconnectErrorToast() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const emulation = useEnterpriseEmulationState();

  return useCallback(
    (err: unknown, providerType?: 'gmail' | 'outlook'): boolean => {
      if (!isReconnectError(err)) return false;

      if (emulation.active) {
        toast({
          title: 'Email connection expired',
          description: `${emulation.target_email} needs to sign in and reconnect their email account — this can't be done from an emulated session.`,
          variant: 'destructive',
        });
        return true;
      }

      const label = providerType ? PROVIDER_LABEL[providerType] : 'email';
      toast({
        title: 'Email connection expired',
        description: `Reconnect your ${label} account to keep sending.`,
        variant: 'destructive',
        duration: 10000,
        action: (
          <ToastAction altText="Go to sender settings to reconnect" onClick={() => navigate('/settings/senders')}>
            Reconnect
          </ToastAction>
        ),
      });
      return true;
    },
    [toast, navigate, emulation],
  );
}
